import { useCallback, useContext, useEffect } from 'react';
import {
  getRandomItem,
  getRandomSet,
  shuffleList,
} from '@/modules/gameUtils/random';
import { AudioAutoPlayerContext } from '@/shared/Players/AudioAutoPlayer/AudioAutoPlayerContext';
import { GameAssetsContext } from '@/shared/GameComponents/GameAssetsContext/GameAssetsContext';
import { GameStageContext } from '@/shared/GameComponents/GameStageContext/GameStageContext';
import memoryGameLayouts from './memoryGameLayouts';
import { getAspectRatioRangeFromLayout } from '@/shared/GameComponents/libs/layout/getAspectRatioRange';

export default function useMemoryGameLoop({ onCorrectAnswer, onFinish, game }) {
  const audioPlayer = useContext(AudioAutoPlayerContext);
  const { state: assets } = useContext(GameAssetsContext);
  const gameStage = useContext(GameStageContext);

  const actions = {
    onCardClick: async (concept) => {
      if (!game.state.playingAudio) {
        const cards = [...game.state.cards];
        const cardIndex = cards.findIndex(
          (card) => card.id == concept.id && card.cardIndex == concept.cardIndex
        );
        const card = cards[cardIndex];
        let flippedCards = [...game.state.flippedCards];
        let gameFinished = false;
        let hasToUnflip = false;
        let feedbackSounds = [];
        if (!card.flipped) {
          switch (game.state.flippedCards.length) {
            case 0:
              cards[cardIndex].flipped = true;
              feedbackSounds.push([assets.sounds[card.id].audio]);
              game.actions.setState({
                flippedCards: [card],
                cards,
              });
              break;
            case 1:
              cards[cardIndex].flipped = true;
              const matchesPreviousCard = flippedCards.find(
                (flippedCard) => flippedCard.id == card.id
              );
              if (matchesPreviousCard) {
                const answersLog = [
                  ...game.state.answersLog,
                  { conceptId: card.id, answerId: card.id, correct: true },
                ];
                const matchedConcepts = [
                  ...game.state.matchedConcepts,
                  card.id,
                ];
                game.actions.setState({
                  flippedCards: [],
                  matchedConcepts,
                  answersLog,
                  cards,
                  lastCorrectIndex: cardIndex,
                });
                feedbackSounds.push(getCorrectFeedbackAudio());
                gameFinished = cards.every((card) =>
                  matchedConcepts.includes(card.id)
                );
                if (!gameFinished) {
                  onCorrectAnswer({ matchedConcepts, cards });
                }
              } else {
                flippedCards = [...flippedCards, card];
                game.actions.setState({
                  flippedCards,
                  lastCorrectIndex: null,
                });
                feedbackSounds.push([assets.sounds[card.id].audio]);
                feedbackSounds.push(getIncorrectFeedbackAudio());
                hasToUnflip = true;
              }
              break;
          }

          game.actions.setState({ playingAudio: true });
          await audioPlayer.actions.play(feedbackSounds);
          game.actions.setState({ playingAudio: false });
          if (hasToUnflip) {
            game.actions.setState({
              flippedCards: [],
              cards: cards.map((card) => ({
                ...card,
                flipped:
                  card.flipped &&
                  !flippedCards.some(
                    (flippedCard) => flippedCard.id == card.id
                  ),
              })),
            });
          }
          if (gameFinished) {
            game.actions.setState({
              finished: true,
            });

            onFinish();
          }
        }
      }
    },
  };

  useEffect(() => {
    let cards = getRandomSet(
      game.state.gameConf.activityData.cards,
      game.state.gameConf.conceptsCount
    );
    cards = [...cards, ...cards];
    cards = shuffleList(cards);
    game.actions.setState({
      randomCards: cards,
    });
  }, []);

  useEffect(() => {
    const randomCards = game.state.randomCards;
    if (randomCards) {
      const cardsAlreadySet = game.state.cards?.length;
      let cards = cardsAlreadySet ? [...game.state.cards] : [...randomCards];
      const conceptsCount = game.state.gameConf.conceptsCount;
      const aspectRatio = gameStage.state.width / (gameStage.state.height || 1);
      const aspectRatioId = getAspectRatioRangeFromLayout(
        aspectRatio,
        memoryGameLayouts[conceptsCount]
      );
      const layout = memoryGameLayouts[conceptsCount][aspectRatioId];
      const size = layout.size;
      const totalRows = layout.cardsPerRow.length;
      const cardARatio = 22 / 43;
      let row = 0;
      let col = 0;
      cards = cards.map((card, index) => {
        const totalCols = layout.cardsPerRow[row];
        const blankSpaceX = 100 - totalCols * size;
        const blankSpaceY = 100 - totalRows * size;
        const gapX = blankSpaceX / totalCols;
        const gapY = blankSpaceY / totalRows;
        const x = gapX / 2 + col * (size + gapX) + size / 2;
        const y = gapY / 2 + row * (size + gapY) + size / 2;

        const transform = {
          x,
          y,
          width: size,
          height: size / cardARatio,
          pivot: { x: 50, y: 50 },
        };

        col++;
        if (col == totalCols) {
          col = 0;
          row++;
        }

        const newCard = {
          ...card,
          transform,
        };

        if (!cardsAlreadySet) {
          newCard.flipped = false;
          newCard.cardIndex = index;
        }

        return newCard;
      });

      game.actions.setState({
        cards,
      });
    }
  }, [game.state.randomCards, gameStage.state.width, gameStage.state.height]);

  const getIncorrectFeedbackAudio = useCallback(() => {
    const incorrectAudios = ['oo', 'oooo'];
    return assets.sounds[getRandomItem(incorrectAudios)].audio;
  }, [assets]);

  const getCorrectFeedbackAudio = useCallback(() => {
    const correctAudios = ['bien', 'muybien', 'bravo', 'perfecto'];
    return assets.sounds[getRandomItem(correctAudios)].audio;
  }, [assets]);

  return { actions };
}
